import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaFutbol, FaTrophy, FaChartLine, FaFire, FaClock, FaTshirt, FaStar, FaUsers, FaHandsHelping } from 'react-icons/fa';
import '../../scss/Stories.scss';
import api from '../../services/apiService';
import confetti from 'canvas-confetti';

const SeasonWrapped = ({ isOpen, onClose }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [progress, setProgress] = useState(0);
  const [wrappedData, setWrappedData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [slides, setSlides] = useState([]);
  const progressInterval = useRef(null);
  const confettiFired = useRef(new Set()); // Track which slides have fired confetti
  const storyDuration = 6000; // 6 seconds per slide

  // Fetch wrapped data
  useEffect(() => {
    const fetchWrappedData = async () => {
      if (!isOpen) return;

      setIsLoading(true);
      try {
        const playerId = sessionStorage.getItem('player_id');
        const teamId = sessionStorage.getItem('selected_team_id');

        if (!playerId || !teamId) {
          console.error('Missing player_id or team_id');
          setIsLoading(false);
          return;
        }

        const response = await api.get(
          `get_season_wrapped.php?player_id=${playerId}&team_id=${teamId}`
        );

        if (response.data && response.data.success) {
          setWrappedData(response.data);
          
          // Create slides from the data
          const createdSlides = createSlidesFromData(response.data);
          setSlides(createdSlides);
        } else {
          console.error('Failed to fetch wrapped data');
        }
      } catch (error) {
        console.error('Error fetching wrapped data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWrappedData();
  }, [isOpen]);

  // Create slides from API data
  const createSlidesFromData = (data) => {
    const slides = [];

    // Slide 1: Total Matches
    if (data.matches) {
      slides.push({
        id: 'matches',
        type: 'matches',
        data: {
          total: data.matches.total,
          busiestMonth: data.matches.busiest_month,
          busiestMonthCount: data.matches.busiest_month_count
        }
      });
    }

    // Slide 2: Goals
    if (data.goals) {
      slides.push({
        id: 'goals',
        type: 'goals',
        data: {
          total: data.goals.total,
          bestMonth: data.goals.best_month,
          bestMonthGoals: data.goals.best_month_goals,
          teamRank: data.goals.team_rank
        }
      });
    }

    // Slide 3: Total Playing Time
    if (data.minutes) {
      slides.push({
        id: 'minutes',
        type: 'minutes',
        data: {
          total: data.minutes.total
        }
      });
    }

    // Slide 4: Favourite Colour
    if (data.goals && data.goals.favourite_colour) {
      slides.push({
        id: 'favourite_colour',
        type: 'favourite_colour',
        data: {
          colour: data.goals.favourite_colour
        }
      });
    }

    // Slide 5: Man of the Match
    if (data.motm) {
      slides.push({
        id: 'motm',
        type: 'motm',
        data: {
          total: data.motm.total,
          teamRank: data.motm.team_rank,
          topPlayer: data.motm.top_player
        }
      });
    }

    // Slide 6: Most Goals in One Game
    if (data.team && data.team.most_goals_in_one_game) {
      slides.push({
        id: 'most_goals_game',
        type: 'most_goals_game',
        data: {
          goals: data.team.most_goals_in_one_game.goals,
          matchDate: data.team.most_goals_in_one_game.match_date,
          opposition: data.team.most_goals_in_one_game.opposition,
          topScorers: data.team.most_goals_in_one_game.top_scorers || []
        }
      });
    }

    // Slide 7: Most Played With
    if (data.team && data.team.most_played_with && data.team.most_played_with.length > 0) {
      slides.push({
        id: 'most_played_with',
        type: 'most_played_with',
        data: {
          players: data.team.most_played_with.slice(0, 3) // Top 3
        }
      });
    }

    // Slide 8: Team Contribution
    if (data.team && data.team.player_goals_percentage !== undefined) {
      slides.push({
        id: 'team_contribution',
        type: 'team_contribution',
        data: {
          percentage: data.team.player_goals_percentage,
          playerGoals: data.goals?.total || 0,
          teamTotalGoals: data.team.team_total_goals || 0
        }
      });
    }

    // Slide 9: Achievements
    if (data.achievements) {
      slides.push({
        id: 'achievements',
        type: 'achievements',
        data: {
          totalUnlocked: data.achievements.total_unlocked,
          teamRank: data.achievements.team_rank,
          firstAchievement: data.achievements.first_achievement,
          timeline: data.achievements.timeline || []
        }
      });
    }

    // Slide 10: Fun Stats
    if (data.fun) {
      slides.push({
        id: 'fun_stats',
        type: 'fun_stats',
        data: {
          earlyBird: data.fun.early_bird,
          earlyBirdMatch: data.fun.early_bird_match,
          isConsistencyKing: data.fun.is_consistency_king,
          peakPerformanceMonth: data.fun.peak_performance_month,
          peakPerformanceDetails: data.fun.peak_performance_details
        }
      });
    }

    // Slide 11: Celebration into 2026 (always add as final slide)
    slides.push({
      id: 'celebration_2026',
      type: 'celebration_2026',
      data: {}
    });

    return slides;
  };

  // Handle auto-progression
  useEffect(() => {
    if (!isOpen || slides.length === 0) {
      return;
    }

    // Clear any existing interval
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }

    // Reset progress when slide changes
    setProgress(0);

    // Start the progress animation after a short delay
    const timerDelay = setTimeout(() => {
      const intervalTime = 50; // Update more frequently for smoother animation
      const increment = (intervalTime / storyDuration) * 100;
      
      progressInterval.current = setInterval(() => {
        setProgress(prev => {
          const newProgress = Math.min(prev + increment, 100);

          if (newProgress >= 100) {
            clearInterval(progressInterval.current);
            progressInterval.current = null;

            // Move to next slide or close
            setTimeout(() => {
              setCurrentSlideIndex(prev => {
                if (prev < slides.length - 1) {
                  return prev + 1;
                } else {
                  onClose();
                  return prev;
                }
              });
            }, 100);

            return 100;
          }
          return newProgress;
        });
      }, intervalTime);
    }, 200);

    return () => {
      clearTimeout(timerDelay);
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    };
  }, [isOpen, slides.length, currentSlideIndex, onClose, storyDuration]);

  // Handle touch events for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    setProgress(0);

    if (isLeftSwipe && currentSlideIndex < slides.length - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    } else if (isRightSwipe && currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  // Handle manual navigation
  const goToPrevSlide = () => {
    if (currentSlideIndex > 0) {
      setProgress(0);
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const goToNextSlide = () => {
    if (currentSlideIndex < slides.length - 1) {
      setProgress(0);
      setCurrentSlideIndex(currentSlideIndex + 1);
    } else {
      onClose();
    }
  };

  // Fire confetti for celebration (only once per slide)
  const fireConfetti = (slideId) => {
    // Check if confetti has already been fired for this slide
    if (confettiFired.current.has(slideId)) {
      return;
    }
    
    // Mark this slide as having fired confetti
    confettiFired.current.add(slideId);
    
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 1001  // Just above container (1000) but content will be above via isolation
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
        scalar: 1.2,
        shapes: ['star', 'circle'],
        colors: ['#ffd700', '#ffa500', '#ffffff', '#ff6b6b'],
        ticks: 200,
        gravity: 1.2,
        decay: 0.94,
        startVelocity: 30,
        spread: 70,
      });
    }

    fire(0.25, {
      spread: 26,
      startVelocity: 55,
    });
    fire(0.2, {
      spread: 60,
    });
    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8
    });
  };

  // Render slide based on type
  const renderSlide = () => {
    if (isLoading) {
      return (
        <div className="story loading-story">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p>Loading your 2025 wrapped...</p>
          </div>
        </div>
      );
    }

    if (slides.length === 0) {
      return (
        <div className="story no-stories">
          <div className="no-stories-content">
            <FaFutbol className="info-icon" />
            <h2>No Data Available</h2>
            <p>Check back later for your 2025 wrapped!</p>
          </div>
        </div>
      );
    }

    const currentSlide = slides[currentSlideIndex];

    switch (currentSlide.type) {
      case 'matches':
        return <MatchesSlide slide={currentSlide} slideId={currentSlide.id} onMount={fireConfetti} />;
      case 'goals':
        return <GoalsSlide slide={currentSlide} slideId={currentSlide.id} onMount={fireConfetti} />;
      case 'minutes':
        return <MinutesSlide slide={currentSlide} slideId={currentSlide.id} onMount={fireConfetti} />;
      case 'favourite_colour':
        return <FavouriteColourSlide slide={currentSlide} slideId={currentSlide.id} onMount={fireConfetti} />;
      case 'motm':
        return <MotmSlide slide={currentSlide} slideId={currentSlide.id} onMount={fireConfetti} />;
      case 'most_goals_game':
        return <MostGoalsGameSlide slide={currentSlide} slideId={currentSlide.id} onMount={fireConfetti} />;
      case 'most_played_with':
        return <MostPlayedWithSlide slide={currentSlide} slideId={currentSlide.id} onMount={fireConfetti} />;
      case 'team_contribution':
        return <TeamContributionSlide slide={currentSlide} slideId={currentSlide.id} onMount={fireConfetti} />;
      case 'achievements':
        return <AchievementsSlide slide={currentSlide} slideId={currentSlide.id} onMount={fireConfetti} />;
      case 'fun_stats':
        return <FunStatsSlide slide={currentSlide} slideId={currentSlide.id} onMount={fireConfetti} />;
      case 'celebration_2026':
        return <Celebration2026Slide slide={currentSlide} slideId={currentSlide.id} onMount={fireConfetti} />;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="stories-container"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="stories-progress-bar">
        {slides.length > 0 ? (
          slides.map((slide, index) => {
            const isCurrent = index === currentSlideIndex;
            const isSeen = index < currentSlideIndex;
            const fillWidth = isCurrent ? progress : isSeen ? 100 : 0;
            const backgroundColor = isSeen ? 'rgba(255, 215, 0, 0.5)' : '#ffd700';
            
            return (
              <div 
                key={index} 
                className={`progress-segment ${isSeen ? 'seen' : isCurrent ? 'unseen' : ''}`}
              >
                <div
                  className="progress-fill"
                  style={{
                    width: `${fillWidth}%`,
                    background: backgroundColor,
                    transition: isCurrent ? 'width 0.1s linear' : 'width 0.3s ease'
                  }}
                />
              </div>
            );
          })
        ) : (
          <div className="progress-segment">
            <div 
              className="progress-fill" 
              style={{ 
                width: `${progress}%`,
                background: '#ffd700',
                transition: 'width 0.1s linear'
              }} 
            />
          </div>
        )}
      </div>

      <div className="stories-content">
        {renderSlide()}
      </div>

      <button className="close-stories" onClick={onClose}>
        <FaTimes />
      </button>

      {slides.length > 1 && (
        <div className="stories-navigation">
          <button
            className="nav-button prev"
            onClick={goToPrevSlide}
            disabled={currentSlideIndex === 0}
          >
            <FaChevronLeft />
          </button>
          <button
            className="nav-button next"
            onClick={goToNextSlide}
          >
            <FaChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

// Matches Slide Component
const MatchesSlide = ({ slide, slideId, onMount }) => {
  const [isVisible, setIsVisible] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      // Only fire confetti once per slide mount
      if (onMount && !confettiFiredRef.current) {
        confettiFiredRef.current = true;
        onMount(slideId);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [slideId, onMount]);

  return (
    <div className="story achievement-story">
      <div className={`achievement-content ${isVisible ? 'visible' : ''}`}>
        <div className="achievement-header">
          <div className="team-name">Your 2025 Wrapped</div>
        </div>
        <div className="achievement-icon">
          <FaFutbol />
        </div>
        <h2>You Played</h2>
        <h3>{slide.data.total} Matches</h3>
        {slide.data.busiestMonth && (
          <p>Busiest month: <strong>{slide.data.busiestMonth}</strong> ({slide.data.busiestMonthCount} matches)</p>
        )}
      </div>
    </div>
  );
};

// Goals Slide Component
const GoalsSlide = ({ slide, slideId, onMount }) => {
  const [isVisible, setIsVisible] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      // Only fire confetti once per slide mount
      if (onMount && !confettiFiredRef.current) {
        confettiFiredRef.current = true;
        onMount(slideId);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [slideId, onMount]);

  return (
    <div className="story achievement-story">
      <div className={`achievement-content ${isVisible ? 'visible' : ''}`}>
        <div className="achievement-header">
          <div className="team-name">Your 2025 Wrapped</div>
        </div>
        <div className="achievement-icon">
          <FaTrophy />
        </div>
        <h2>You Scored</h2>
        <h3>{slide.data.total} Goals</h3>
        {slide.data.bestMonth && (
          <p>Best month: <strong>{slide.data.bestMonth}</strong> ({slide.data.bestMonthGoals} goals)</p>
        )}
        {slide.data.teamRank && (
          <p>Ranked #{slide.data.teamRank} in your team</p>
        )}
      </div>
    </div>
  );
};

// Minutes Slide Component
const MinutesSlide = ({ slide, slideId, onMount }) => {
  const [isVisible, setIsVisible] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      if (onMount && !confettiFiredRef.current) {
        confettiFiredRef.current = true;
        onMount(slideId);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [slideId, onMount]);

  // Convert minutes to hours and minutes
  const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
    }
    return `${mins} minute${mins !== 1 ? 's' : ''}`;
  };

  return (
    <div className="story achievement-story">
      <div className={`achievement-content ${isVisible ? 'visible' : ''}`}>
        <div className="achievement-header">
          <div className="team-name">Your 2025 Wrapped</div>
        </div>
        <div className="achievement-icon">
          <FaClock />
        </div>
        <h2>Total Playing Time</h2>
        <h3>{formatMinutes(parseInt(slide.data.total))}</h3>
      </div>
    </div>
  );
};

// Favourite Colour Slide Component
const FavouriteColourSlide = ({ slide, slideId, onMount }) => {
  const [isVisible, setIsVisible] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      if (onMount && !confettiFiredRef.current) {
        confettiFiredRef.current = true;
        onMount(slideId);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [slideId, onMount]);

  return (
    <div className="story achievement-story">
      <div className={`achievement-content ${isVisible ? 'visible' : ''}`}>
        <div className="achievement-header">
          <div className="team-name">Your 2025 Wrapped</div>
        </div>
        <div className="achievement-icon">
          <FaTshirt />
        </div>
        <h2>Your Favourite Colour</h2>
        <h3>{slide.data.colour}</h3>
        <p>to play in</p>
      </div>
    </div>
  );
};

// MOTM Slide Component
const MotmSlide = ({ slide, slideId, onMount }) => {
  const [isVisible, setIsVisible] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      if (onMount && !confettiFiredRef.current) {
        confettiFiredRef.current = true;
        onMount(slideId);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [slideId, onMount]);

  return (
    <div className="story achievement-story">
      <div className={`achievement-content ${isVisible ? 'visible' : ''}`}>
        <div className="achievement-header">
          <div className="team-name">Your 2025 Wrapped</div>
        </div>
        <div className="achievement-icon">
          <FaStar />
        </div>
        <h2>Man of the Match</h2>
        <h3>{slide.data.total} MOTM</h3>
        {slide.data.teamRank && (
          <p>Ranked #{slide.data.teamRank} in your team</p>
        )}
        {slide.data.topPlayer && (
          <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(255, 215, 0, 0.1)', borderRadius: '10px' }}>
            <p style={{ margin: '5px 0', fontSize: '0.9rem', color: '#a0a0a0' }}>Top MOTM Winner:</p>
            <p style={{ margin: '5px 0', fontSize: '1.1rem', color: '#ffd700', fontWeight: 'bold' }}>
              {slide.data.topPlayer.name}
            </p>
            <p style={{ margin: '5px 0', fontSize: '0.9rem', color: '#a0a0a0' }}>
              {slide.data.topPlayer.motm_count} MOTM awards
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Most Goals in One Game Slide Component
const MostGoalsGameSlide = ({ slide, slideId, onMount }) => {
  const [isVisible, setIsVisible] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      if (onMount && !confettiFiredRef.current) {
        confettiFiredRef.current = true;
        onMount(slideId);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [slideId, onMount]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="story achievement-story">
      <div className={`achievement-content ${isVisible ? 'visible' : ''}`}>
        <div className="achievement-header">
          <div className="team-name">Team Stats</div>
        </div>
        <div className="achievement-icon">
          <FaFutbol />
        </div>
        <h2>Most Goals in One Game</h2>
        <h3>{slide.data.goals} Goals</h3>
        {slide.data.matchDate && (
          <p style={{ marginTop: '10px', fontSize: '0.9rem', color: '#a0a0a0' }}>
            {formatDate(slide.data.matchDate)}
          </p>
        )}
        {slide.data.opposition && (
          <p style={{ fontSize: '0.9rem', color: '#a0a0a0' }}>
            vs {slide.data.opposition}
          </p>
        )}
        {slide.data.topScorers && slide.data.topScorers.length > 0 && (
          <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(255, 215, 0, 0.1)', borderRadius: '10px', textAlign: 'left' }}>
            <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#ffd700', fontWeight: 'bold' }}>Top Scorers:</p>
            {slide.data.topScorers.slice(0, 5).map((scorer, index) => (
              <div key={index} style={{ margin: '5px 0' }}>
                <p style={{ margin: '0', fontSize: '0.9rem', color: '#fff' }}>
                  {index + 1}. {scorer.name} - {scorer.goals || slide.data.goals} {scorer.goals === 1 || (scorer.goals || slide.data.goals) === 1 ? 'goal' : 'goals'}
                </p>
                {scorer.match_date && (
                  <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: '#a0a0a0', fontStyle: 'italic' }}>
                    {formatDate(scorer.match_date)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Most Played With Slide Component
const MostPlayedWithSlide = ({ slide, slideId, onMount }) => {
  const [isVisible, setIsVisible] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      if (onMount && !confettiFiredRef.current) {
        confettiFiredRef.current = true;
        onMount(slideId);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [slideId, onMount]);

  return (
    <div className="story achievement-story">
      <div className={`achievement-content ${isVisible ? 'visible' : ''}`}>
        <div className="achievement-header">
          <div className="team-name">Team Stats</div>
        </div>
        <div className="achievement-icon">
          <FaUsers />
        </div>
        <h2>Most Played With</h2>
        {slide.data.players && slide.data.players.length > 0 ? (
          <div style={{ marginTop: '20px' }}>
            {slide.data.players.map((player, index) => (
              <div key={player.player_id || index} style={{ marginBottom: '15px', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
                <p style={{ margin: '0', fontSize: '1.1rem', color: '#ffd700', fontWeight: 'bold' }}>
                  {index + 1}. {player.name}
                </p>
                {player.times_together && (
                  <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', color: '#a0a0a0' }}>
                    {player.times_together} time{player.times_together !== 1 ? 's' : ''} together
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p>No data available</p>
        )}
      </div>
    </div>
  );
};

// Team Contribution Slide Component
const TeamContributionSlide = ({ slide, slideId, onMount }) => {
  const [isVisible, setIsVisible] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      if (onMount && !confettiFiredRef.current) {
        confettiFiredRef.current = true;
        onMount(slideId);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [slideId, onMount]);

  // Format contribution percentage
  const formatContribution = (percentage) => {
    if (typeof percentage === 'string' && percentage.includes('%')) {
      return percentage;
    }
    if (typeof percentage === 'number') {
      return `${percentage.toFixed(1)}%`;
    }
    return percentage;
  };

  return (
    <div className="story achievement-story">
      <div className={`achievement-content ${isVisible ? 'visible' : ''}`}>
        <div className="achievement-header">
          <div className="team-name">Team Stats</div>
        </div>
        <div className="achievement-icon">
          <FaTrophy />
        </div>
        <h2>Team Contribution</h2>
        <h3>{formatContribution(slide.data.percentage)}</h3>
        <p style={{ marginTop: '10px', fontSize: '0.9rem', color: '#a0a0a0' }}>
          You scored {slide.data.playerGoals} of {slide.data.teamTotalGoals} team goals
        </p>
      </div>
    </div>
  );
};

// Achievements Slide Component
const AchievementsSlide = ({ slide, slideId, onMount }) => {
  const [isVisible, setIsVisible] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      if (onMount && !confettiFiredRef.current) {
        confettiFiredRef.current = true;
        onMount(slideId);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [slideId, onMount]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="story achievement-story">
      <div className={`achievement-content ${isVisible ? 'visible' : ''}`}>
        <div className="achievement-header">
          <div className="team-name">Your 2025 Wrapped</div>
        </div>
        <div className="achievement-icon">
          <FaTrophy />
        </div>
        <h2>Achievements</h2>
        <h3>{slide.data.totalUnlocked || 0} Unlocked</h3>
        {slide.data.teamRank && (
          <p style={{ marginTop: '10px', fontSize: '0.9rem', color: '#a0a0a0' }}>
            Ranked #{slide.data.teamRank} in your team
          </p>
        )}
        {slide.data.firstAchievement && (
          <p style={{ marginTop: '15px', fontSize: '0.85rem', color: '#ffd700', fontStyle: 'italic' }}>
            First achievement: {formatDate(slide.data.firstAchievement.date)}
          </p>
        )}
        {slide.data.timeline && slide.data.timeline.length > 0 && (
          <p style={{ marginTop: '10px', fontSize: '0.8rem', color: '#a0a0a0' }}>
            {slide.data.timeline.length} achievement{slide.data.timeline.length !== 1 ? 's' : ''} earned this season
          </p>
        )}
      </div>
    </div>
  );
};

// Fun Stats Slide Component
const FunStatsSlide = ({ slide, slideId, onMount }) => {
  const [isVisible, setIsVisible] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      if (onMount && !confettiFiredRef.current) {
        confettiFiredRef.current = true;
        onMount(slideId);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [slideId, onMount]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="story achievement-story">
      <div className={`achievement-content ${isVisible ? 'visible' : ''}`}>
        <div className="achievement-header">
          <div className="team-name">Your 2025 Wrapped</div>
        </div>
        <div className="achievement-icon">
          <FaFire />
        </div>
        <h2>Fun Stats</h2>
        <div style={{ marginTop: '20px', textAlign: 'left', maxWidth: '90%' }}>
          {slide.data.earlyBird && (
            <p style={{ margin: '10px 0', fontSize: '0.9rem', color: '#fff' }}>
              <span style={{ color: '#ffd700' }}>Early Bird:</span> {slide.data.earlyBird}
            </p>
          )}
          {slide.data.isConsistencyKing && (
            <p style={{ margin: '10px 0', fontSize: '0.9rem', color: '#fff' }}>
              <span style={{ color: '#ffd700' }}>Consistency King:</span> You maintained consistent attendance throughout the season!
            </p>
          )}
          {slide.data.peakPerformanceMonth && (
            <p style={{ margin: '10px 0', fontSize: '0.9rem', color: '#fff' }}>
              <span style={{ color: '#ffd700' }}>Peak Performance:</span> {slide.data.peakPerformanceMonth}
              {slide.data.peakPerformanceDetails && (
                <span style={{ fontSize: '0.8rem', color: '#a0a0a0', display: 'block', marginTop: '5px' }}>
                  {slide.data.peakPerformanceDetails.goals} goals, {slide.data.peakPerformanceDetails.motm_count} MOTM, {Math.round(slide.data.peakPerformanceDetails.attendance_pct)}% attendance
                </span>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Celebration 2026 Slide Component
const Celebration2026Slide = ({ slide, slideId, onMount }) => {
  const [isVisible, setIsVisible] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      if (onMount && !confettiFiredRef.current) {
        confettiFiredRef.current = true;
        // Fire extra confetti for celebration
        onMount(slideId);
        setTimeout(() => onMount(slideId), 500);
        setTimeout(() => onMount(slideId), 1000);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [slideId, onMount]);

  return (
    <div className="story achievement-story">
      <div className={`achievement-content ${isVisible ? 'visible' : ''}`}>
        <div className="achievement-header">
          <div className="team-name">Your 2025 Wrapped</div>
        </div>
        <div className="achievement-icon" style={{ fontSize: '4rem' }}>
          🎉
        </div>
        <h2 style={{ fontSize: '2.5rem', marginTop: '20px' }}>Here's to 2026!</h2>
        <h3 style={{ fontSize: '1.8rem', marginTop: '20px', color: '#ffd700' }}>Keep Playing, Keep Scoring!</h3>
        <p style={{ marginTop: '30px', fontSize: '1rem', color: '#a0a0a0', fontStyle: 'italic' }}>
          Thank you for an amazing 2025 season
        </p>
        <p style={{ marginTop: '10px', fontSize: '0.9rem', color: '#fff' }}>
          See you on the pitch! ⚽
        </p>
      </div>
    </div>
  );
};

export default SeasonWrapped;

